/* Searchable model dropdown — see snippets/product-model-dropdown.liquid.
 *
 * Presentation only. Selection is made by native <label for> -> radio change, which
 * the section's own variant handler (product-section-3.liquid) already listens for.
 * This file never writes a variant id, a price or a form field.
 */
(function () {
  'use strict';

  if (window.__modelSelectInit) return;
  window.__modelSelectInit = true;

  var MIN_WIDTH = 260;
  var MAX_WIDTH = 360;
  var GUTTER = 16;
  var OFFSET = 6;
  var CLOSE_MS = 300;

  var instances = [];
  var openInstance = null;

  function create(root) {
    var trigger = root.querySelector('.model-select__trigger');
    var panel = root.querySelector('.model-select__panel');
    if (!trigger || !panel) return null;

    var instance = {
      root: root,
      trigger: trigger,
      panel: panel,
      valueEl: root.querySelector('.model-select__value'),
      search: panel.querySelector('.model-select__search-input'),
      list: panel.querySelector('.model-select__list'),
      empty: panel.querySelector('.model-select__empty'),
      options: Array.prototype.slice.call(panel.querySelectorAll('.model-select__option')),
      groups: Array.prototype.slice.call(panel.querySelectorAll('.model-select__group')),
      optionName: root.getAttribute('data-option-name') || '',
      portaled: false,
      closeTimer: null,
      isOpen: false
    };

    trigger.addEventListener('click', function () {
      if (instance.isOpen) close(instance);
      else open(instance);
    });

    trigger.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'Down') {
        event.preventDefault();
        open(instance);
      }
    });

    if (instance.search) {
      instance.search.addEventListener('input', function () {
        filter(instance);
      });
      instance.search.addEventListener('keydown', function (event) {
        onSearchKeydown(instance, event);
      });
    }

    // A row was picked: the radio's own change event drives the variant update.
    panel.addEventListener('change', function () {
      close(instance);
      instance.trigger.focus();
    });

    panel.addEventListener('mousemove', function (event) {
      var option = event.target.closest && event.target.closest('.model-select__option');
      if (option) setActive(instance, option);
    });

    return instance;
  }

  /* Open / close
     ---------------------------------------------------------------- */

  function open(instance) {
    if (openInstance && openInstance !== instance) close(openInstance);

    if (instance.closeTimer) {
      clearTimeout(instance.closeTimer);
      instance.closeTimer = null;
    }

    // .product-info-left / .product-info-right set both `transform` and
    // `overflow-y: auto`, which clips even fixed-position descendants. Move the
    // panel to <body> so it can escape. Done lazily so the section's own
    // DOMContentLoaded init has already walked the fieldsets.
    if (!instance.portaled) {
      document.body.appendChild(instance.panel);
      instance.portaled = true;
    }

    instance.panel.hidden = false;
    instance.trigger.setAttribute('aria-expanded', 'true');
    instance.isOpen = true;
    openInstance = instance;

    if (instance.search) instance.search.value = '';
    filter(instance);
    position(instance);

    requestAnimationFrame(function () {
      instance.panel.classList.add('is-open');
    });

    var selected = instance.panel.querySelector('.model-select__option[data-selected="true"]');
    if (selected) {
      setActive(instance, selected);
      selected.scrollIntoView({ block: 'nearest' });
    }

    // Focusing the field on a touch device pops the keyboard over the list.
    if (instance.search && window.matchMedia('(hover: hover)').matches) {
      instance.search.focus();
    }
  }

  function close(instance) {
    if (!instance.isOpen) return;

    instance.isOpen = false;
    instance.panel.classList.remove('is-open');
    instance.trigger.setAttribute('aria-expanded', 'false');
    if (openInstance === instance) openInstance = null;

    instance.closeTimer = setTimeout(function () {
      instance.panel.hidden = true;
      instance.closeTimer = null;
    }, CLOSE_MS);
  }

  function position(instance) {
    var rect = instance.trigger.getBoundingClientRect();

    // Trigger no longer laid out (its copy of the block got hidden) — nothing to anchor to.
    if (!rect.width && !rect.height) {
      close(instance);
      return;
    }

    var viewportW = document.documentElement.clientWidth;
    var viewportH = document.documentElement.clientHeight;

    var width = Math.max(rect.width, MIN_WIDTH);
    width = Math.min(width, MAX_WIDTH, viewportW - GUTTER * 2);

    var left = rect.left;
    if (left + width > viewportW - GUTTER) left = viewportW - GUTTER - width;
    if (left < GUTTER) left = GUTTER;

    var below = viewportH - rect.bottom - OFFSET - GUTTER;
    var above = rect.top - OFFSET - GUTTER;
    var flip = below < 220 && above > below;
    var space = flip ? above : below;
    var maxHeight = Math.min(space, viewportH * 0.6);

    instance.panel.style.width = width + 'px';
    instance.panel.style.left = left + 'px';
    instance.panel.style.maxHeight = Math.max(maxHeight, 160) + 'px';

    if (flip) {
      instance.panel.style.top = Math.max(rect.top - OFFSET - maxHeight, GUTTER) + 'px';
    } else {
      instance.panel.style.top = rect.bottom + OFFSET + 'px';
    }
  }

  /* Filtering
     ---------------------------------------------------------------- */

  function filter(instance) {
    var query = instance.search ? instance.search.value.trim().toLowerCase() : '';
    var tokens = query ? query.split(/\s+/) : [];
    var matches = 0;

    instance.options.forEach(function (option) {
      var haystack = option.getAttribute('data-search') || '';
      var visible = tokens.every(function (token) {
        return haystack.indexOf(token) !== -1;
      });
      option.hidden = !visible;
      option.classList.remove('is-active');
      if (visible) matches++;
    });

    instance.groups.forEach(function (group) {
      var visible = Array.prototype.some.call(
        group.querySelectorAll('.model-select__option'),
        function (option) {
          return !option.hidden;
        }
      );
      group.hidden = !visible;
    });

    if (instance.empty) instance.empty.hidden = matches !== 0;
    if (instance.list) instance.list.scrollTop = 0;

    if (tokens.length && matches) {
      var first = instance.options.find(function (option) {
        return !option.hidden;
      });
      if (first) setActive(instance, first);
    }
  }

  /* Keyboard
     ---------------------------------------------------------------- */

  function visibleOptions(instance) {
    return instance.options.filter(function (option) {
      return !option.hidden;
    });
  }

  function setActive(instance, option) {
    instance.options.forEach(function (other) {
      other.classList.toggle('is-active', other === option);
    });
  }

  function move(instance, delta) {
    var visible = visibleOptions(instance);
    if (!visible.length) return;

    var current = visible.findIndex(function (option) {
      return option.classList.contains('is-active');
    });
    var next = current === -1 ? (delta > 0 ? 0 : visible.length - 1) : current + delta;
    if (next < 0) next = visible.length - 1;
    if (next >= visible.length) next = 0;

    setActive(instance, visible[next]);
    visible[next].scrollIntoView({ block: 'nearest' });
  }

  function onSearchKeydown(instance, event) {
    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        event.preventDefault();
        move(instance, 1);
        break;
      case 'ArrowUp':
      case 'Up':
        event.preventDefault();
        move(instance, -1);
        break;
      case 'Home': {
        var first = visibleOptions(instance)[0];
        if (first) {
          event.preventDefault();
          setActive(instance, first);
          first.scrollIntoView({ block: 'nearest' });
        }
        break;
      }
      case 'End': {
        var all = visibleOptions(instance);
        if (all.length) {
          event.preventDefault();
          setActive(instance, all[all.length - 1]);
          all[all.length - 1].scrollIntoView({ block: 'nearest' });
        }
        break;
      }
      case 'Enter': {
        event.preventDefault();
        var active = instance.panel.querySelector('.model-select__option.is-active');
        if (active && !active.hidden) active.click();
        break;
      }
      case 'Escape':
      case 'Esc':
        event.preventDefault();
        close(instance);
        instance.trigger.focus();
        break;
      default:
        break;
    }
  }

  /* Keeping both copies of the block in sync
     ----------------------------------------------------------------
     The section renders the variant block twice (mobile + desktop) and the radios
     have no form owner, so both copies share one document-wide radio group:
     checking one unchecks its twin. The selected row therefore cannot rely on
     `:checked + label` CSS and is marked from here instead. */

  function syncSelection(optionName, value) {
    instances.forEach(function (instance) {
      if (instance.optionName !== optionName) return;

      if (instance.valueEl) instance.valueEl.textContent = value;

      instance.options.forEach(function (option) {
        if (option.getAttribute('data-value') === value) {
          option.setAttribute('data-selected', 'true');
        } else {
          option.removeAttribute('data-selected');
        }
      });
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-model-select]');
    if (!roots.length) return;

    Array.prototype.forEach.call(roots, function (root) {
      var instance = create(root);
      if (instance) instances.push(instance);
    });
    if (!instances.length) return;

    document.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.classList || !input.classList.contains('variant-input')) return;
      var match = (input.name || '').match(/options\[(.*?)\]/);
      if (match) syncSelection(match[1], input.value);
    });

    document.addEventListener('click', function (event) {
      if (!openInstance) return;
      if (openInstance.panel.contains(event.target)) return;
      if (openInstance.trigger.contains(event.target)) return;
      close(openInstance);
    });

    document.addEventListener('keydown', function (event) {
      if (!openInstance) return;
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      var instance = openInstance;
      close(instance);
      instance.trigger.focus();
    });

    var pending = false;
    var reposition = function () {
      if (!openInstance || pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        if (openInstance) position(openInstance);
      });
    };
    document.addEventListener('scroll', reposition, true);

    // The block is rendered twice and CSS swaps which copy is visible at 1024px,
    // so a resize can pull the anchor out from under an open panel.
    window.addEventListener('resize', function () {
      if (openInstance) close(openInstance);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
